import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Upload, Link2, HardDrive } from "lucide-react";
import api from "@/lib/api";
import { DEPARTMENTS, DEFAULT_EVENT_LABELS } from "./galleryConstants";

const ACCEPTED = ["image/jpeg", "image/png"];
const MAX_BYTES = 10 * 1024 * 1024;
const inputCls = "w-full px-4 py-2.5 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#1E1E1E]/60 mb-2";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const PhotoModal = ({ photo, eventTitles, existingLabels, onClose, onSaved }) => {
  const isEdit = !!photo?.id;
  const [data, setData] = useState({
    url: photo?.url || "", caption: photo?.caption || "", order: photo?.order ?? 0, published: photo?.published ?? true,
    department: photo?.department || "General Church", event_label: photo?.event_label || "", photo_date: photo?.photo_date || "",
    original_filename: photo?.original_filename || "", file_id: photo?.file_id || "", source: photo?.source || "url",
  });
  const [mode, setMode] = useState(isEdit ? "url" : "upload"); // upload | url | drive
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [driveLink, setDriveLink] = useState("");
  const [newLabel, setNewLabel] = useState(false);
  const [saving, setSaving] = useState(false);

  const eventOptions = useMemo(() => {
    const all = [...(eventTitles || []), ...(existingLabels || []), ...DEFAULT_EVENT_LABELS];
    return [...new Set(all.filter(Boolean))];
  }, [eventTitles, existingLabels]);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ACCEPTED.includes(f.type) || !["jpg", "jpeg", "png"].includes(ext)) {
      toast.error("That file type isn't supported. Please choose a .jpg, .jpeg, or .png photo.");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("That photo is larger than 10 MB. Please choose a smaller file.");
      return;
    }
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let payload = { ...data, order: Number(data.order) || 0 };
      if (mode === "upload" && file) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        payload = { ...payload, ...r.data };
      } else if (mode === "drive" && driveLink.trim()) {
        const r = await api.post("/gallery/import-url", { url: driveLink.trim() });
        payload = { ...payload, ...r.data };
      } else if (mode === "url") {
        if (!payload.url) throw new Error("Please add an image URL or upload a photo.");
        if (!isEdit || payload.url !== photo.url) payload = { ...payload, source: "url", file_id: "", original_filename: "" };
      } else if (!isEdit) {
        throw new Error(mode === "upload" ? "Please choose a photo to upload." : "Please paste a Google Drive link.");
      }
      const { url, caption, order, published, department, event_label, photo_date, original_filename, file_id, source } = payload;
      const body = { url, caption, order, published, department, event_label, photo_date, original_filename, file_id, source };
      if (isEdit) await api.put(`/gallery/${photo.id}`, body);
      else await api.post("/gallery", body);
      toast.success(isEdit ? "Photo updated" : "Photo added to gallery");
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const previewSrc = mode === "upload" && filePreview ? filePreview : mode === "url" ? data.url : isEdit ? data.url : "";

  return (
    <div data-testid="admin-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
          <h3 className="font-display text-xl font-bold text-[#003B5C]">{isEdit ? "Edit" : "New"} Photo</h3>
          <button type="button" onClick={onClose} data-testid="admin-modal-close" className="p-2 hover:bg-gray-100 rounded-sm"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className={labelCls}>Photo Source</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "upload", label: "Upload", icon: Upload },
                { k: "url", label: "Image URL", icon: Link2 },
                { k: "drive", label: "Google Drive", icon: HardDrive },
              ].map(({ k, label, icon: Icon }) => (
                <button key={k} type="button" onClick={() => setMode(k)} data-testid={`photo-source-${k}`}
                  className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-wider border rounded-sm transition-colors ${mode === k ? "bg-[#003B5C] text-white border-[#003B5C]" : "border-gray-200 text-[#003B5C] hover:border-[#003B5C]"}`}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>

          {mode === "upload" && (
            <div>
              <label htmlFor="photo-file-input" data-testid="photo-file-dropzone" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-[#003B5C] rounded-sm p-6 cursor-pointer text-center">
                <Upload size={20} className="text-[#003B5C]" />
                <span className="text-sm text-[#1E1E1E]/80">{file ? "Choose a different photo" : "Click to choose a photo from your computer"}</span>
                <span className="text-xs text-[#1E1E1E]/50">Accepted: .jpg, .jpeg, .png · Max 10 MB</span>
              </label>
              <input id="photo-file-input" type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={onFile} data-testid="photo-file-input" className="hidden" />
              {file && (
                <div data-testid="photo-file-info" className="mt-3 text-xs text-[#1E1E1E]/70 grid grid-cols-3 gap-2 bg-[#F7F8FA] border border-gray-200 rounded-sm p-3">
                  <div><span className="font-bold uppercase tracking-wider text-[#1E1E1E]/50 block">Name</span><span data-testid="photo-file-name" className="truncate block">{file.name}</span></div>
                  <div><span className="font-bold uppercase tracking-wider text-[#1E1E1E]/50 block">Type</span><span data-testid="photo-file-type">{file.type}</span></div>
                  <div><span className="font-bold uppercase tracking-wider text-[#1E1E1E]/50 block">Size</span><span data-testid="photo-file-size">{fmtSize(file.size)}</span></div>
                </div>
              )}
              {isEdit && !file && <p className="text-xs text-[#1E1E1E]/55 mt-2">Leave empty to keep the current photo.</p>}
            </div>
          )}

          {mode === "url" && (
            <div>
              <label className={labelCls}>Image URL {isEdit ? "" : "*"}</label>
              <input type="url" value={data.url} onChange={set("url")} placeholder="https://…/photo.jpg" data-testid="modal-input-photo-url" className={inputCls} />
              <p className="text-xs text-[#1E1E1E]/55 mt-2">Use a permanent, publicly accessible image link. Temporary Facebook CDN links can expire.</p>
            </div>
          )}

          {mode === "drive" && (
            <div>
              <label className={labelCls}>Google Drive share link *</label>
              <input type="url" value={driveLink} onChange={(e) => setDriveLink(e.target.value)} placeholder="https://drive.google.com/file/d/…/view?usp=sharing" data-testid="modal-input-drive-link" className={inputCls} />
              <p className="text-xs text-[#1E1E1E]/55 mt-2">In Google Drive, right-click the JPG/PNG → Share → set to <strong>Anyone with the link</strong> → Copy link. The image is downloaded and saved into the website's own storage.</p>
            </div>
          )}

          {previewSrc && (
            <div className="bg-[#F7F8FA] border border-gray-200 rounded-sm p-3">
              <div className={labelCls}>Preview</div>
              <div className="aspect-video bg-white rounded-sm overflow-hidden">
                <img src={previewSrc} alt="preview" data-testid="photo-preview-image" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            </div>
          )}

          <div>
            <label className={labelCls}>Caption</label>
            <input type="text" value={data.caption} onChange={set("caption")} placeholder="e.g. Sabbath worship — Sept 2026" data-testid="modal-input-photo-caption" className={inputCls} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Department</label>
              <select value={data.department} onChange={set("department")} data-testid="modal-input-photo-department" className={inputCls}>
                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Event / Category</label>
              {newLabel ? (
                <div className="flex gap-2">
                  <input type="text" autoFocus value={data.event_label} onChange={set("event_label")} placeholder="Type a new event label" data-testid="modal-input-photo-event-new" className={inputCls} />
                  <button type="button" onClick={() => setNewLabel(false)} className="text-xs text-[#003B5C] underline whitespace-nowrap">List</button>
                </div>
              ) : (
                <select value={data.event_label} data-testid="modal-input-photo-event"
                  onChange={(e) => { if (e.target.value === "__new__") { setNewLabel(true); setData((d) => ({ ...d, event_label: "" })); } else setData((d) => ({ ...d, event_label: e.target.value })); }}
                  className={inputCls}>
                  <option value="">— No event —</option>
                  {eventOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  <option value="__new__">+ Add a new event label…</option>
                </select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Photo / Event Date</label>
              <input type="date" value={data.photo_date} onChange={set("photo_date")} data-testid="modal-input-photo-date" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Sort Order</label>
              <input type="number" value={data.order} onChange={set("order")} data-testid="modal-input-photo-order" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Visibility</label>
              <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-sm cursor-pointer hover:border-[#003B5C]">
                <input type="checkbox" checked={data.published} onChange={(e) => setData((d) => ({ ...d, published: e.target.checked }))} data-testid="modal-input-photo-published" className="accent-[#003B5C]" />
                <span className="text-sm text-[#1E1E1E]">Published</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-[#1E1E1E]/70 hover:text-[#003B5C]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="modal-save-button" className="bg-[#003B5C] text-white px-6 py-2.5 rounded-sm text-sm font-medium hover:bg-[#002A42] disabled:opacity-60">
              {saving ? "Saving…" : isEdit ? "Update" : "Save Photo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
